// Angular + External
import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  HostBinding,
  NgZone,
} from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
//BFX
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { BfxAuthService } from "../bfx-common/services/bfx-auth.service";
import { BfxPrimeService } from "../bfx-common/services/bfx-prime.service";

import {
  ConnectionStatus,
  ConnectionStatusPromiseService,
  ConnectionStatusState,
} from "shared/broker-client-services";
import { ProfilePromiseActionService, ProfileSettingKeys } from "shared/cubiq-services";

const THEME_KEY = ProfileSettingKeys.userThemeSettings;
const LANG_KEY = ProfileSettingKeys.userLanguageSettings;

/**
 * The LoaderComponent is responsible for the handling the service loading screen.
 */

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { TranslateService } from "@ngx-translate/core";
import { LoaderService } from "./loader.service";
import { DEFAULT_CONTEXT_PATH } from "./logout.component";

@Component({
  selector: "bfx-loader",
  templateUrl: "./html/loader.component.html",
  standalone: false,
})
export class LoaderComponent implements OnInit, AfterViewInit, OnDestroy {
  public theme: string = "";

  @HostBinding("class.ready-visibility-hook")
  public readonly readyVisibilityHook: boolean = true;

  private themeSubscription: Subscription;

  public get frameTag() {
    return document.body.dataset.frameTag;
  }

  public unrecoverableError: boolean = false;

  /**********************************************************************/
  /* DATA */

  // Config
  public config: any = {
    debug: false,
    routes: {
      home: "main",
      next: "main",
      error: "not-found",
    },
  };
  // Priming
  public progress = {
    mode: "determinate",
    log: {
      show: false,
      text: "",
    },
    visual: {
      error: 0,
      primed: 0,
      total: 8,
      value: 0,
    },
  };
  // server status
  public connectionStatus: number;
  public connectionStatusText: string = "Initializing";

  // Subscriptions
  private uiConfigSubscription: Subscription;
  private serverStatusSubscription: Subscription;
  private primeControlSubscription: Subscription;

  /**********************************************************************/
  /* CONSTRUCTOR + LIFE HOOKS */
  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _authService: BfxAuthService,
    private _configService: BfxConfigService,
    private _primeService: BfxPrimeService,
    public profileActionService: ProfilePromiseActionService,
    public cd: ChangeDetectorRef,
    private _connectionStatusService: ConnectionStatusPromiseService,
    private ngZone: NgZone,
    private translateService: TranslateService,
    private _loaderService: LoaderService,
  ) {
    this.subscribeToConfiguration();
  }

  ngOnInit() {
    this.initializeNextRoute();
    NativeDesktopSupport.initWindowPools();
  }

  ngAfterViewInit() {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let wnd = NativeDesktopSupport.getNativeDesktopWindow();
      wnd.updateOptions({ resizable: false });
    }

    Promise.all([
      this.profileActionService.getProfileConfig(THEME_KEY),
      this.profileActionService.getProfileConfig(LANG_KEY),
    ]).then(([cssTheme, lang]) => {
      this.subscribeToPrimeControl();
      this.subscribeToServerStatus();
      this._loaderService.setCssTheme(cssTheme);

      if (!lang) {
        lang = "en";
      }
      this.translateService.use(lang);
      this.translateService.setDefaultLang(lang);
      this.invokePrime();
    });
  }

  ngOnDestroy() {
    if (this.uiConfigSubscription != null) {
      this.uiConfigSubscription.unsubscribe();
      this.uiConfigSubscription = null;
    }

    if (this.serverStatusSubscription != null) {
      this.serverStatusSubscription.unsubscribe();
      this.serverStatusSubscription = null;
    }
  }

  /**********************************************************************/
  /* INITIALIZATION */
  private subscribeToConfiguration(): void {
    this.uiConfigSubscription = this._configService.uiConfig$.subscribe(
      (config) => {
        this.config.debug = config.general.debug;
        this.config.homeRoute = config.general.homeRoute;
        this.progress.log.show = config.auth.showLoadingLog;
      },
      (reject) => {
        logger.out.error(
          "ERROR: LoaderComponent.subscribeToConfiguration(): configuration load fail | err:",
          reject,
        );
      },
    );
  }

  private subscribeToPrimeControl(): void {
    this.primeControlSubscription = this._primeService.primeControl$.subscribe((response) => {
      this.processPrimeControlEvent(response);
    });
  }

  private subscribeToServerStatus(): void {
    this.serverStatusSubscription = this._connectionStatusService
      .getConnectionEvents()
      .subscribe((event: ConnectionStatus) => {
        this.ngZone.run(() => {
          // only first connection is valid
          // all subsequent events are unrecoverable during this component's lifecycle.
          const valid =
            event.getStatus() == ConnectionStatusState.CONNECTED && this.connectionStatus == null;

          if (event != null) {
            this.connectionStatus = event.getStatus();
            switch (this.connectionStatus) {
              case ConnectionStatusState.NONE:
                this.connectionStatusText = "Initializing"; // none
                break;
              case ConnectionStatusState.CONNECTED:
                this.connectionStatusText = "Online";
                break;
              case ConnectionStatusState.ERROR:
                this.connectionStatusText = "Error";
                break;
              case ConnectionStatusState.DISCONNECTED:
              case ConnectionStatusState.RECONNECTING:
              case ConnectionStatusState.RELOAD:
                this.connectionStatusText = "Reconnecting";
                break;
            }
          }

          this.unrecoverableError = !valid;
        });
      });
  }

  private initializeNextRoute(): void {
    this.config.routes.next = this._route.snapshot.params["nextRoute"];
    if (!this.config.routes.next) {
      // if undefined set to default home route
      this.config.routes.next = this.config.routes.home;
    }
  }

  /**********************************************************************/
  /* PUBLIC METHODS */
  public invokePrime(): void {
    logger.out.info(`${this.constructor.name}.invokePrime()`);

    this._primeService
      .prime()
      .then(
        (resolve) => {
          this.config && this.config.debug
            ? logger.out.info("Loader.component: invokePrime()=", resolve)
            : "";
          if (resolve) {
            this.progress.visual.value = 100;
            if (!this.unrecoverableError) {
              this._router.navigate([this.config.routes.next]);
            }
          } else {
            this.unrecoverableError = true;
          }
        },
        (reject) => {
          logger.out.error("ERROR: Loader.component: invokePrime()= ", reject);
          this.unrecoverableError = true;
        },
      )
      .finally(() => this.cd.detectChanges());
  }

  /**********************************************************************/
  /* PRIVATE METHODS */

  private processPrimeControlEvent(_controlEvent: any): void {
    this.config.debug
      ? logger.out.info(
          "LoaderComponent received primeControl event ",
          _controlEvent.cmd,
          _controlEvent.payload,
        )
      : "";
    switch (_controlEvent.cmd) {
      case "primed":
        this.updateLog(_controlEvent.payload.service, 1);
        break;
      case "fail":
        this.updateLog(_controlEvent.payload.service, 0);
        break;
      case "error":
        this.updateLog(_controlEvent.payload.service, -1);
        break;
      default:
        logger.out.error(
          "ERROR: NotificationViewer.processNotificationCommand(): Unknown control command=",
          _controlEvent.cmd,
        );
        break;
    }
    this.updateLoadingProgress(_controlEvent.payload);
  }

  private updateLoadingProgress(payload: any): void {
    this.progress.visual.primed = payload.primed;
    this.progress.visual.error = payload.error;
    this.progress.visual.total = payload.total;
    this.progress.visual.value = Math.min(
      (this.progress.visual.primed / this.progress.visual.total) * 100,
      100,
    );
    this.cd.detectChanges();
    this.config && this.config.debug
      ? logger.out.info(
          "INFO: LoaderComponent.updateLoadingProgress(): progress.visual.value= ",
          this.progress.visual.value,
        )
      : "";
  }
  private updateLog(_serviceName: string, _resolve: number): void {
    let result: string;
    if (_resolve === 1) {
      result = "OK";
    } else if (_resolve == 0) {
      result = "FAIL";
    } else {
      result = "ERR";
    }
    setTimeout(() => {
      this.progress.log.text += _serviceName + "....<strong>" + result + "</strong></br>";
    }, 4);
  }

  public reroute(): void {
    let contextPath: string = this._authService.getSSOContextPath();

    if (contextPath == null || contextPath.trim().length == 0) {
      contextPath = DEFAULT_CONTEXT_PATH;
    }

    window.location.replace(contextPath);
  }
}
